import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from "../../../../components/ui/dropdown-menu";
import React, { useMemo, useState, useEffect } from "react";
import EngagementHistoryModal from "../../modals/engagementHistoryModal";
import MoveToFolderMenu from "../MoveToFolderMenu";
import RecommendModal from "../../modals/recommendModal/RecommendModal";
import { PaymentIframe } from "../../../payment/PaymentIframe";
import ActivityMetadataSheet from "./ActivityMetadataSheet";
import MarketplaceExamplesModal from "../../../marketplace/MarketplaceExamplesModal";
import { useTranslation } from "react-i18next";
import {
  Eye,
  Play,
  Share2,
  FolderInput,
  Star,
  ShoppingCart,
  Bell,
  Clock,
  Lightbulb,
  Sparkles,
  BarChart3,
  MoreHorizontal,
  Pencil,
  PenLine,
  Link,
  ClipboardList,
  Lock,
  UserPlus,
  Download,
  Upload,
  Table2,
  Archive,
  Trash2,
  MoreVertical,
  Users, BookOpen,
} from "lucide-react";
import useAuth from "../../../../hooks/useAuth";
import UnfinishedEngagementModal from "../../modals/unfinishedEngagementModal";
import { toast } from "sonner";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useProcess from "../../../../hooks/useProcess";
import { config } from "../../../../Environment";
import {
  EngagementTaggingDialog,
  type EngagementTaggingResult,
} from "../../../runActivity/modals/EngagementTaggingDialog";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";
import { SetReminderModal } from "../../../notifications/SetReminderModal";
import ExampleManagement from "../../../examples/ExampleManagement";
import { EXPERT_ROLE } from "../../../../lib/roles";

const ENGAGEMENT_HISTORY_STORAGE_KEY = "engagementHistoryModalState";

const SingleActivity = ({
  prescription,
  prescriptions,
  setPrescriptions,
  index,
  editPrescription,
  viewPrescription,
  renamePrescription,
  deletePrescription,
  copyPrescriptionAsExample,
  sharePrescription,
  managePermissions,
  expertShareWorkflow,
  exportPrescription,
  runPrescription,
  refreshLists,
  isMobile,
  metadata,
  onMoveToFolder,
  onToggleFavorite,
  folders,
}: any) => {
  const { auth, user } = useAuth();

  const [unfinishedEngagementModal, setUnfinishedEngagementModal] =
    useState(false);
  const [showRunTaggingDialog, setShowRunTaggingDialog] = useState(false);
  const [engagementHistoryModal, setEngagementHistoryModal] = useState(false);
  const [recommendModal, setRecommendModal] = useState(false);
  const [metadataSheetOpen, setMetadataSheetOpen] = useState(false);
  const [shareability, setShareability] = useState<{
    shareable: boolean;
    copyLinkAllowed: boolean;
    productId?: string;
    reason?: string;
  } | null>(null);
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
  const [isRenewing, setIsRenewing] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [examplesModalOpen, setExamplesModalOpen] = useState(false);
  const [manageExamplesOpen, setManageExamplesOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const { startProcess, resumeProcess } = useProcess();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isCurrentUserExpert = Array.isArray(user?.roles) && user.roles.includes(EXPERT_ROLE);

  const handleStartNew = () => {
    if (isCurrentUserExpert) {
      setShowRunTaggingDialog(true);
    } else {
      startProcess({
        prescriptionId: prescription._id,
        from: location,
        forward: location,
        onError: () => {
          toast.error("An error occurred while starting the prescription.");
        },
      });
    }
  };

  const handleRunTaggingConfirm = async (result: EngagementTaggingResult) => {
    setShowRunTaggingDialog(false);
    await startProcess({
      prescriptionId: prescription._id,
      engagementName: result.engagementName,
      tags: result.tags,
      clientIdentifier: result.clientIdentifier,
      from: location,
      forward: location,
      onError: () => {
        toast.error("An error occurred while starting the prescription.");
      },
    });
  };

  const runningProcessCount = useMemo(() => {
    if (Array.isArray(prescription.processes)) {
      return prescription.processes.filter(
        (process: any) => process.status === "RUNNING",
      ).length;
    }
    return 0;
  }, [prescription.processes]);

  const continueProcess = async (process: any) => {
    let processToContinue = process._id;
    if (process.pendingInnerProcess) {
      processToContinue = processToContinue.pendingInnerProcess;
    }
    await resumeProcess({
      processId: processToContinue,
      from: location,
      forward: location,
      onError: () => {
        toast.error(
          "An error occurred while continuing the process. Please try again.",
        );
        refreshLists();
      },
    });
  };
  const usageLimitReached = useMemo(() => {
    // Debug: log usage data
    if (prescription.usageLimit) {
      console.log(
        `[SingleActivity] ${prescription.name} - usageLimit: ${
          prescription.usageLimit
        }, usageCount: ${prescription.usageCount || 0}`,
      );
    }
    return (
      prescription.usageLimit &&
      (prescription.usageCount || 0) >= prescription.usageLimit
    );
  }, [prescription.usageLimit, prescription.usageCount, prescription.name]);

  // Fetch shareability status
  useEffect(() => {
    const fetchShareability = async () => {
      if (!auth.email || !prescription._id) return;

      try {
        const response = await retryFetch(
          `${config.API_BASE}/api/prescription/${prescription._id}/shareable`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (response.ok) {
          const data = await response.json();
          setShareability(data);
        }
      } catch (error) {
        console.error("Failed to fetch shareability:", error);
      }
    };

    fetchShareability();
  }, [prescription._id, auth.email]);

  // Check localStorage and URL params to restore engagement history modal state
  useEffect(() => {
    // First, check localStorage
    try {
      const stored = localStorage.getItem(ENGAGEMENT_HISTORY_STORAGE_KEY);
      if (stored) {
        const { prescriptionId, isOpen } = JSON.parse(stored);
        if (
          prescriptionId === prescription._id &&
          isOpen &&
          !engagementHistoryModal
        ) {
          setEngagementHistoryModal(true);
          return; // If we opened from localStorage, we're done
        }
      }
    } catch (error) {
      console.error(
        "Error reading engagement history state from localStorage:",
        error,
      );
    }

    // Fallback to URL params
    const ehPrescriptionId = searchParams.get("ehPrescriptionId");
    if (ehPrescriptionId === prescription._id && !engagementHistoryModal) {
      setEngagementHistoryModal(true);
    } else if (!ehPrescriptionId && engagementHistoryModal) {
      // Only close modal if params are removed AND localStorage doesn't indicate it should be open
      try {
        const stored = localStorage.getItem(ENGAGEMENT_HISTORY_STORAGE_KEY);
        if (!stored) {
          setEngagementHistoryModal(false);
        }
      } catch (error) {
        setEngagementHistoryModal(false);
      }
    }
  }, [searchParams, prescription._id, location.pathname, location.key]);

  const handleNameClick = (prescription: any) => {
    if (!prescription.valid) return;
    if (
      (runningProcessCount > 0 &&
        prescription?.settings?.engagementMode === "ask") ||
      (runningProcessCount > 0 && !prescription?.settings)
    ) {
      setUnfinishedEngagementModal(true);
    } else if (
      prescription?.settings &&
      prescription?.settings?.engagementMode === "same" &&
      runningProcessCount > 0
    ) {
      const latestInProgressProcess = prescription.processes.find(
        (process: any) => process.status === "RUNNING",
      );
      continueProcess(latestInProgressProcess);
    } else if (
      prescription?.settings &&
      prescription?.settings?.engagementMode === "same" &&
      runningProcessCount === 0 &&
      prescription.settings.resumeFinished === true &&
      Array.isArray(prescription.processes) &&
      prescription.processes.length > 0
    ) {
      continueProcess(prescription.processes[0]);
    } else {
      handleStartNew();
    }
  };

  const handleCopyLink = async () => {
    try {
      const activityUrl = `${window.location.origin}/share/activity?wid=${prescription._id}`;
      await navigator.clipboard.writeText(activityUrl);
      toast.success(
        t("msg_link_copied_to_clipboard") || "Link copied to clipboard!",
      );
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error(t("failed_to_copy_link") || "Failed to copy link");
    }
  };

  const handleRenew = async () => {
    try {
      setIsRenewing(true);
      const response = await retryFetch(
        `${config.API_BASE}/api/payment/renewActivityUsage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            workflowId: prescription._id,
            useIframe: true,
          }),
        },
      );

      const result = await response.json();

      if (result.type === "redirect" && result.redirect) {
        // Open payment in iframe
        setPaymentUrl(result.redirect);
        setShowPaymentIframe(true);
        setIsRenewing(false);
      } else {
        toast.error(
          t("payment_initialization_failed") || "Failed to initialize payment",
        );
        setIsRenewing(false);
      }
    } catch (error) {
      console.error("Failed to initiate renewal:", error);
      toast.error(t("renewal_failed") || "Failed to initiate renewal");
      setIsRenewing(false);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentIframe(false);
    toast.success(
      t("renewal_successful") ||
        "Renewal successful! Your usage limit has been increased.",
    );
    // Refresh the lists to update the usage limit
    if (refreshLists) {
      refreshLists();
    }
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
    setShowPaymentIframe(false);
  };

  const handleMetadataSave = async (data: {
    name: string;
    description: string;
    objectives: string[];
  }) => {
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/${prescription._id}/metadata`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(data),
        },
      );

      if (response.ok) {
        toast.success(
          t("metadata_saved") || "Activity details saved successfully!",
        );
        // Refresh the lists to get updated data
        if (refreshLists) {
          refreshLists();
        }
      } else {
        const errorData = await response.json();
        toast.error(
          errorData.error ||
            t("failed_to_save") ||
            "Failed to save activity details",
        );
      }
    } catch (error) {
      console.error("Failed to save metadata:", error);
      toast.error(t("failed_to_save") || "Failed to save activity details");
    }
  };

  const isAuthor = prescription.isAuthor;
  const isExpert = prescription.isExpert;
  const isEditor = prescription.isAuthor || prescription.isCollaborator;

  return (
    <>
      <div
        className={`relative bg-white rounded-2xl border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(13,148,136,0.10)] hover:border-primary/25 hover:-translate-y-0.5 transition-all duration-300 ease-out overflow-hidden flex flex-col group ${
          !prescription.valid ? "opacity-50" : ""
        }`}
      >
        {/* Subtle decorative background shapes — varied per card */}
        {(() => {
          const id = prescription._id || "";
          const hash = (id.charCodeAt(id.length - 1) + id.charCodeAt(id.length - 2)) % 4;
          const patterns = [
            // Pattern 0: lavender + mint scattered circles
            <>
              <div className="absolute -top-6 end-8 w-20 h-20 rounded-full bg-violet-200/20 group-hover:bg-violet-200/30 transition-colors duration-500" />
              <div className="absolute top-1/2 -start-4 w-12 h-12 rounded-full bg-emerald-200/15 group-hover:bg-emerald-200/25 transition-colors duration-500" />
              <div className="absolute -bottom-3 end-1/3 w-16 h-16 rounded-full bg-sky-200/15 group-hover:bg-sky-200/25 transition-colors duration-500" />
              <div className="absolute top-4 start-1/3 w-24 h-[2px] rotate-[25deg] bg-violet-300/20 group-hover:bg-violet-300/30 transition-colors duration-500" />
            </>,
            // Pattern 1: peach + sky diamonds and dots
            <>
              <div className="absolute top-3 end-1/4 w-14 h-14 rotate-45 rounded-lg bg-amber-200/15 group-hover:bg-amber-200/25 transition-colors duration-500" />
              <div className="absolute bottom-4 start-8 w-10 h-10 rotate-45 rounded-md bg-sky-200/15 group-hover:bg-sky-200/25 transition-colors duration-500" />
              <div className="absolute top-1/2 end-6 w-3 h-3 rounded-full bg-rose-300/25 group-hover:bg-rose-300/40 transition-colors duration-500" />
              <div className="absolute top-6 start-4 w-2 h-2 rounded-full bg-violet-300/20 group-hover:bg-violet-300/35 transition-colors duration-500" />
              <div className="absolute bottom-8 end-12 w-2 h-2 rounded-full bg-emerald-300/20 group-hover:bg-emerald-300/35 transition-colors duration-500" />
            </>,
            // Pattern 2: rose + indigo flowing arcs
            <>
              <div className="absolute -top-8 start-1/4 w-28 h-28 rounded-full border-[2px] border-rose-200/20 group-hover:border-rose-200/35 transition-colors duration-500" />
              <div className="absolute -bottom-6 end-1/4 w-20 h-20 rounded-full border-[2px] border-indigo-200/15 group-hover:border-indigo-200/30 transition-colors duration-500" />
              <div className="absolute top-1/3 end-3 w-4 h-4 rounded-full bg-amber-200/25 group-hover:bg-amber-200/40 transition-colors duration-500" />
              <div className="absolute bottom-1/3 start-6 w-3 h-3 rounded-full bg-teal-200/20 group-hover:bg-teal-200/35 transition-colors duration-500" />
            </>,
            // Pattern 3: teal + violet bars and dots
            <>
              <div className="absolute top-5 -start-6 w-16 h-[2px] rounded-full rotate-[170deg] bg-teal-300/20 group-hover:bg-teal-300/30 transition-colors duration-500" />
              <div className="absolute top-1/3 end-1/3 w-12 h-12 rounded-full bg-rose-100/20 group-hover:bg-rose-100/35 transition-colors duration-500" />
              <div className="absolute -bottom-4 start-1/3 w-14 h-14 rounded-full bg-sky-200/15 group-hover:bg-sky-200/25 transition-colors duration-500" />
              <div className="absolute top-8 end-8 w-2.5 h-2.5 rounded-full bg-amber-300/25 group-hover:bg-amber-300/40 transition-colors duration-500" />
              <div className="absolute bottom-10 start-10 w-2 h-2 rounded-full bg-emerald-300/20 group-hover:bg-emerald-300/35 transition-colors duration-500" />
            </>,
          ];
          return (
            <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
              {patterns[hash]}
            </div>
          );
        })()}

        {/* Card Header */}
        <div className="relative p-3 sm:p-4 border-gray-100 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="mb-2">
                <button
                  onClick={() => handleNameClick(prescription)}
                  className="font-semibold text-left w-full line-clamp-3 text-gray-900 transition-colors text-sm sm:text-[15px] leading-snug group-hover:text-primary active:text-primary/80 touch-manipulation"
                  title={prescription.name}
                >
                  {prescription.name}
                </button>
                {(prescription as any).__isPwf && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      const pwfId = (prescription as any).fiPwfId ?? prescription._id;
                      navigate(`/pwf?id=${pwfId}`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const pwfId = (prescription as any).fiPwfId ?? prescription._id;
                        navigate(`/pwf?id=${pwfId}`);
                      }
                    }}
                    className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 border-violet-200 mt-1 cursor-pointer hover:bg-violet-200 transition-colors"
                    title="Open in PWF Hub"
                  >
                    PWF
                  </span>
                )}
              </div>

              {/* Upcoming Reminder */}
              {prescription.upcomingReminder &&
                (() => {
                  // Use app's language for date formatting
                  const scheduledTime = DateTime.fromISO(
                    prescription.upcomingReminder.scheduledDateTime,
                  ).setLocale(i18n.language);
                  const isPast = scheduledTime < DateTime.now();
                  return (
                    <Badge
                      variant="outline"
                      className={`mb-2 text-[11px] sm:text-xs py-0.5 px-1.5 sm:px-2 cursor-pointer transition-all hover:shadow-sm inline-flex items-center gap-1.5 w-fit ${
                        isPast
                          ? "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800"
                          : "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10 dark:bg-primary/20 dark:text-primary/70 dark:border-primary/80"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setReminderModalOpen(true);
                      }}
                    >
                      <Bell className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-medium">
                        {t("reminder")}:{" "}
                        {scheduledTime.toRelative({ locale: i18n.language })}
                      </span>
                    </Badge>
                  );
                })()}

              {/* Folder indicator */}
              {metadata?.folderName && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: metadata.folderColor || "#6B7280",
                    }}
                  />
                  <span>{metadata.folderName}</span>
                </div>
              )}

              {/* Collection shared indicator */}
              {prescription.collectionName && (
                <Badge
                  variant="outline"
                  className="mb-2 text-[11px] sm:text-xs bg-primary/5 text-primary border-primary/20"
                >
                  <Users className="w-3 h-3 me-1.5 flex-shrink-0" />
                  {t("shared_from") || "Shared from"}:{" "}
                  {prescription.collectionName}
                </Badge>
              )}

              <div className="flex flex-wrap gap-2">
                {prescription.usageLimit && (
                  <Badge
                    variant="outline"
                    className={`text-xs inline-flex items-center gap-1 ${
                      (prescription.usageCount || 0) >= prescription.usageLimit
                        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                    }`}
                  >
                    <span>
                      {prescription.usageCount || 0}/{prescription.usageLimit}{" "}
                      {t("uses") || "uses"}
                    </span>
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {onToggleFavorite && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(prescription._id);
                  }}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-300 transition-colors hover:text-yellow-500 active:scale-[0.9]"
                >
                  <Star
                    className={`w-4 h-4 ${
                      metadata?.isFavorite
                        ? "fill-yellow-500 text-yellow-500"
                        : ""
                    }`}
                  />
                </button>
              )}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-50 transition-all duration-200 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                  <MoreVertical className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                {/* Section 1: Primary actions */}
                {!!prescription.valid && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => {
                      if (
                        prescription?.settings?.engagementMode === "same" &&
                        runningProcessCount > 0
                      ) {
                        const latestInProgressProcess =
                          prescription.processes.find(
                            (process: any) => process.status === "RUNNING",
                          );
                        continueProcess(latestInProgressProcess);
                      } else {
                        handleNameClick(prescription);
                      }
                    }}
                  >
                    <Play className="w-4 h-4 me-2" />
                    {t("action_start", "Start")}
                  </DropdownMenuItem>
                )}
                {!isMobile && isEditor && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => editPrescription(prescription._id)}
                  >
                    <Pencil className="w-4 h-4 me-2" />
                    {t("edit")}
                  </DropdownMenuItem>
                )}

                {/* Section 2: Organize */}
                <DropdownMenuSeparator />
                {auth.email && !isMobile && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => renamePrescription(prescription._id)}
                  >
                    <PenLine className="w-4 h-4 me-2" />
                    {t("rename")}
                  </DropdownMenuItem>
                )}
                {onMoveToFolder && folders && folders.length > 0 && (
                  <MoveToFolderMenu
                    folders={folders}
                    onMoveToFolder={(folderId) =>
                      onMoveToFolder(prescription._id, folderId)
                    }
                    currentFolderId={metadata?.folderId}
                  />
                )}
                {auth.email && !!prescription.valid && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => setReminderModalOpen(true)}
                  >
                    <Bell className="w-4 h-4 me-2" />
                    {t("set_reminder")}
                  </DropdownMenuItem>
                )}

                {/* Section 3: Share & History */}
                {auth.email && !!prescription.valid && (
                  <>
                    <DropdownMenuSeparator />
                    {isAuthor && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={handleCopyLink}
                      >
                        <Link className="w-4 h-4 me-2" />
                        {t("copy_link")}
                      </DropdownMenuItem>
                    )}
                    {!!prescription.valid && shareability?.productId && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => setRecommendModal(true)}
                      >
                        <Star className="w-4 h-4 me-2" />
                        {t("recommend_activity")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => setEngagementHistoryModal(true)}
                    >
                      <Eye className="w-4 h-4 me-2" />
                      {t("engagement_history")}
                    </DropdownMenuItem>
                  </>
                )}

                {/* Analytics on mobile (desktop has it under More...) */}
                {auth.email && !!prescription.valid && isMobile && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/insights?wid=${encodeURIComponent(prescription._id.toString())}`
                        )
                      }
                    >
                      <BarChart3 className="w-4 h-4 me-2" />
                      {t("analytics")}
                    </DropdownMenuItem>
                  </>
                )}

                {/* Section 4: More... submenu for secondary items */}
                {auth.email && !!prescription.valid && !isMobile && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <MoreHorizontal className="w-4 h-4 me-2" />
                        {t("more_actions", "More...")}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {isEditor && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => setMetadataSheetOpen(true)}
                          >
                            <ClipboardList className="w-4 h-4 me-2" />
                            {t("edit_details") || "Edit Details"}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() =>
                            navigate(
                              `/insights?wid=${encodeURIComponent(prescription._id.toString())}`
                            )
                          }
                        >
                          <BarChart3 className="w-4 h-4 me-2" />
                          {t("analytics")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => setExamplesModalOpen(true)}
                        >
                          <BookOpen className="w-4 h-4 me-2" />
                          {t("view_examples") || "View Examples"}
                        </DropdownMenuItem>
                        {isAuthor && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => setManageExamplesOpen(true)}
                          >
                            <Lightbulb className="w-4 h-4 me-2" />
                            {t("manage_examples") || "Manage Examples"}
                          </DropdownMenuItem>
                        )}
                        {(isAuthor || isExpert) && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => managePermissions(prescription._id)}
                          >
                            <Lock className="w-4 h-4 me-2" />
                            {t("manage_permissions")}
                          </DropdownMenuItem>
                        )}
                        {isExpert && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => expertShareWorkflow(prescription._id)}
                          >
                            <UserPlus className="w-4 h-4 me-2" />
                            {t("share")}
                          </DropdownMenuItem>
                        )}
                        {isAuthor && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => exportPrescription(prescription._id)}
                            >
                              <Download className="w-4 h-4 me-2" />
                              {t("export")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={() => copyPrescriptionAsExample(prescription._id)}
                            >
                              <Upload className="w-4 h-4 me-2" />
                              {t("copy_activity_as_example")}
                            </DropdownMenuItem>
                          </>
                        )}
                        {(prescription.componentFlags?.data_event ||
                          prescription.componentFlags?.data_event_analysis) && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/dataEvent?wid=${encodeURIComponent(
                                  prescription._id.toString(),
                                )}`,
                              )
                            }
                          >
                            <Table2 className="w-4 h-4 me-2" />
                            {t("data_event")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </>
                )}

                {/* Section 5: Destructive — always last */}
                {isAuthor && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => deletePrescription(prescription._id)}
                    >
                      {prescription.versions > 0 ? (
                        <>
                          <Archive className="w-4 h-4 me-2" />
                          {t("archive")}
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 me-2" />
                          {t("delete")}
                        </>
                      )}
                    </DropdownMenuItem>
                  </>
                )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Card Footer - Quick Actions */}
        <div className="relative flex items-center gap-3 px-3 pt-2 pb-3 sm:px-4">
          {prescription.valid && (
            <>
              {usageLimitReached ? (
                <div className="flex gap-2">
                  <button
                    disabled
                    className="flex items-center gap-1.5 opacity-40 cursor-not-allowed text-xs font-medium text-primary px-2.5 py-1.5 rounded-md touch-manipulation"
                  >
                    <Play className="w-3 h-3" />
                    {t("action_start", "Start")}
                  </button>
                  {shareability?.productId && (
                    <Button
                      size="sm"
                      onClick={handleRenew}
                      disabled={isRenewing}
                      className="h-7 text-xs text-white bg-green-600 hover:bg-green-700 rounded-md px-3 active:scale-[0.97] transition-all duration-150"
                    >
                      <ShoppingCart className="w-3 h-3 me-1" />
                      {isRenewing ? t("loading") : t("renew") || "Renew"}
                    </Button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handleNameClick(prescription)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 px-3 py-1.5 rounded-full active:scale-[0.97] transition-all duration-150 touch-manipulation min-h-[36px] shadow-sm"
                >
                  <Play className="w-3 h-3 fill-current" />
                  {t("action_start", "Start")}
                </button>
              )}
              {auth.email && (
                <button
                  onClick={() => setEngagementHistoryModal(true)}
                  className="flex items-center gap-1 min-h-[36px] px-1.5 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-colors active:scale-[0.97] touch-manipulation"
                >
                  <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-xs tabular-nums">{prescription.engagementCount || 0}</span>
                  {runningProcessCount > 0 && (
                    <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium leading-none">
                      {runningProcessCount} {t("active")}
                    </span>
                  )}
                </button>
              )}
            </>
          )}
          {!prescription.valid && (
            <div className="text-xs italic text-gray-400">
              {t("activity_not_valid")}
            </div>
          )}
        </div>
      </div>

      {/* Unfinished Process Modal */}
      {engagementHistoryModal && (
        <EngagementHistoryModal
          engagementHistoryModal={engagementHistoryModal}
          setEngagementHistoryModal={setEngagementHistoryModal}
          prescription={prescription}
          refreshLists={refreshLists}
          setPrescriptions={setPrescriptions}
        />
      )}

      {/* History Modal */}
      {unfinishedEngagementModal && (
        <UnfinishedEngagementModal
          unfinishedEngagementModal={unfinishedEngagementModal}
          setUnfinishedEngagementModal={setUnfinishedEngagementModal}
          prescription={prescription}
          setPrescriptions={setPrescriptions}
          refreshLists={refreshLists}
        />
      )}

      {/* Recommend Modal */}
      {recommendModal && (
        <RecommendModal
          open={recommendModal}
          onClose={() => setRecommendModal(false)}
          workflowId={prescription._id}
          workflowName={prescription.name}
        />
      )}

      {/* Payment Iframe for Renewal */}
      {showPaymentIframe && (
        <PaymentIframe
          open={showPaymentIframe}
          paymentUrl={paymentUrl}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onClose={() => setShowPaymentIframe(false)}
        />
      )}

      {/* Activity Metadata Sheet */}
      {metadataSheetOpen && (
        <ActivityMetadataSheet
          isOpen={metadataSheetOpen}
          onClose={() => setMetadataSheetOpen(false)}
          activityName={prescription.name}
          activityDescription={prescription.description || ""}
          activityObjectives={prescription.objectives || []}
          onSave={handleMetadataSave}
        />
      )}

      {/* Set/Manage Reminder Modal */}
      {reminderModalOpen && (
        <SetReminderModal
          open={reminderModalOpen}
          onOpenChange={setReminderModalOpen}
          prescriptionId={prescription._id}
          activityTitle={prescription.name}
          existingReminder={prescription.upcomingReminder || null}
          onReminderUpdated={refreshLists}
        />
      )}

      {/* Manage Examples Sheet */}
      {manageExamplesOpen && (
        <ExampleManagement
          open={manageExamplesOpen}
          onOpenChange={setManageExamplesOpen}
          activityId={prescription._id}
          mode="preview"
        />
      )}

      {/* View Examples Modal */}
      {examplesModalOpen && (
        <MarketplaceExamplesModal
          open={examplesModalOpen}
          onOpenChange={setExamplesModalOpen}
          workflowId={prescription._id}
          activityName={prescription.name}
        />
      )}

      {/* Tagging dialog — shown before starting a new engagement */}
      <EngagementTaggingDialog
        isOpen={showRunTaggingDialog}
        onConfirm={handleRunTaggingConfirm}
        onClose={() => setShowRunTaggingDialog(false)}
        activityName={prescription.name}
        confirmLabel={t("start_activity") || "Start Activity"}
        showEngagementNameField={true}
        showMentorOptions={isCurrentUserExpert}
      />
    </>
  );
};

export default SingleActivity;
